import { useState, useEffect } from "react";
import axios from "axios";
import { Shield, ShieldCheck, MapPin, Factory, Plus, X, MagnifyingGlass } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Toaster, toast } from "@/components/ui/sonner";
import { NavTabs } from "@/components/NavTabs";
import { SapConnectionStatus } from "@/components/SapConnectionStatus";
import { ErpConnectionStatus } from "@/components/ErpConnectionStatus";
import { FloorLoginsTab } from "@/components/FloorLoginsTab";
import { useAuth, PAGE_LABELS } from "@/contexts/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PAGE_KEYS = Object.keys(PAGE_LABELS);

// Sep 5 2026, user's explicit ask: group the permission checkboxes by the
// same "Work Center" menu sections the nav uses (NavTabs.jsx), instead of
// one long flat list, so a specific right is easy to find. Any key not
// listed below (e.g. a page not linked into the nav yet) falls into "Other".
const WORK_CENTER_GROUPS = [
  { label: "BOM & Production", keys: ["bom_explorer", "production_plan", "production_confirmation", "production_confirmation_test", "consumption_register"] },
  { label: "Inventory Management", keys: ["inventory", "stock_transfer", "inbound_stock_transfer", "store_approval", "npd_store_request", "npd_tooling_head_approval", "npd_tooling_management_approval", "movement_audit_trail", "wip_by_stage", "physical_inventory"] },
  { label: "Master Data", keys: ["admin"] },
  { label: "Administration", keys: ["admin_sap_write", "admin_bom_copy", "admin_boo", "admin_routing_builder", "machine_master"] },
  { label: "Quality", keys: ["quality_decision"] },
];
const GROUPED_PAGE_KEYS = new Set(WORK_CENTER_GROUPS.flatMap((g) => g.keys));
const OTHER_PAGE_KEYS = PAGE_KEYS.filter((k) => !GROUPED_PAGE_KEYS.has(k));

// Aug 2026, user's explicit ask: a new "admin" tier between "user" and
// "super_admin" - full page access automatically like super_admin, can
// grant/revoke pages and set someone's role, but can NEVER hand out
// "admin"/"super_admin" (only an existing super_admin can) and can't
// touch a super_admin's row at all (enforced server-side too, see
// server.py's admin_update_user_access - this is display/UX only).
const ROLE_BADGE = {
  super_admin: { label: "Super Admin", className: "bg-[#0E7C86] text-white" },
  admin: { label: "Admin", className: "bg-[#175CD3] text-white" },
  user: { label: "User", className: "bg-[#F2F4F7] text-[#344054]" },
};

export default function AccessManagementPage() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drafts, setDrafts] = useState({});
  const [savingId, setSavingId] = useState(null);
  // Sep 5 2026, user's explicit ask ("filter by Access right, not role"):
  // filter the User Access list down to only people who currently hold a
  // specific permission - "all" shows everyone.
  const [accessFilter, setAccessFilter] = useState("all");
  // Oct 20 2026, user's explicit ask: find someone by name or email instead of
  // scrolling the whole sign-in list. Works WITH the access-right filter above,
  // never instead of it, and is purely client-side over the users already loaded.
  const [userSearch, setUserSearch] = useState("");

  // Store Binding (Aug 2026, user's explicit ask): restricts a "store
  // user" to only the site(s) bound here on the Store Assignment tab -
  // admin/super_admin are never restricted, so this only matters for
  // plain "user" role rows.
  const [knownSites, setKnownSites] = useState([]);
  const [siteDrafts, setSiteDrafts] = useState({});
  const [siteSavingId, setSiteSavingId] = useState(null);

  // Work Center Assignment (Sep 25 2026, user's explicit ask; extended
  // Sep 27 2026 to be Site-scoped - "I can assign user site wise
  // department which is link to BOO because BOO also based on site" -
  // a user's grant is now a (Site, Department) PAIR, matched against the
  // BOO operation's own Site+Department (boo_service.py). Empty = unrestricted.
  const [workCenters, setWorkCenters] = useState([]);
  const [sites, setSites] = useState([]);
  const [wcDrafts, setWcDrafts] = useState({});
  const [wcSavingId, setWcSavingId] = useState(null);
  const [newWorkCenter, setNewWorkCenter] = useState("");
  const [newSite, setNewSite] = useState("");
  // Oct 10 2026, user's ask for the testing period: master switch for every
  // per-user Production Confirmation restriction (Department/Site grants AND
  // the "earlier operation not finished" sequence lock). Ships OFF.
  const [enforceRestrictions, setEnforceRestrictions] = useState(false);
  const [savingEnforcement, setSavingEnforcement] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data }, { data: sitesData }, { data: wcData }] = await Promise.all([
        axios.get(`${API}/admin/users`),
        axios.get(`${API}/admin/known-sites`),
        axios.get(`${API}/admin/work-centers`),
      ]);
      setUsers(data.users || []);
      setKnownSites(sitesData.sites || []);
      setWorkCenters(wcData.work_centers || []);
      setSites(wcData.sites || []);
      setEnforceRestrictions(!!wcData.enforce_user_restrictions);
      const nextDrafts = {};
      const nextSiteDrafts = {};
      const nextWcDrafts = {};
      (data.users || []).forEach((u) => {
        nextDrafts[u._id] = { role: u.role || "user", allowed_pages: u.allowed_pages || [] };
        nextSiteDrafts[u._id] = u.bound_sites || [];
        nextWcDrafts[u._id] = u.assigned_site_departments || [];
      });
      setDrafts(nextDrafts);
      setSiteDrafts(nextSiteDrafts);
      setWcDrafts(nextWcDrafts);
    } catch (e) {
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const togglePage = (userId, pageKey) => {
    setDrafts((prev) => {
      const current = prev[userId];
      const has = current.allowed_pages.includes(pageKey);
      return {
        ...prev,
        [userId]: {
          ...current,
          allowed_pages: has ? current.allowed_pages.filter((p) => p !== pageKey) : [...current.allowed_pages, pageKey],
        },
      };
    });
  };

  const setRole = (userId, role) => {
    setDrafts((prev) => ({ ...prev, [userId]: { ...prev[userId], role } }));
  };

  const isDirty = (u) => {
    const d = drafts[u._id];
    if (!d) return false;
    if (d.role !== (u.role || "user")) return true;
    const original = new Set(u.allowed_pages || []);
    const draft = new Set(d.allowed_pages);
    return original.size !== draft.size || [...original].some((p) => !draft.has(p));
  };

  const save = async (userId) => {
    setSavingId(userId);
    try {
      await axios.put(`${API}/admin/users/${userId}/access`, drafts[userId]);
      toast.success("Access updated");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update access");
    } finally {
      setSavingId(null);
    }
  };

  const toggleSite = (userId, site) => {
    setSiteDrafts((prev) => {
      const current = prev[userId] || [];
      const has = current.includes(site);
      return { ...prev, [userId]: has ? current.filter((s) => s !== site) : [...current, site] };
    });
  };

  const isSiteDirty = (u) => {
    const original = new Set(u.bound_sites || []);
    const draft = new Set(siteDrafts[u._id] || []);
    return original.size !== draft.size || [...original].some((s) => !draft.has(s));
  };

  const saveSites = async (userId) => {
    setSiteSavingId(userId);
    try {
      await axios.put(`${API}/admin/users/${userId}/store-sites`, { bound_sites: siteDrafts[userId] || [] });
      toast.success("Store site binding updated");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update site binding");
    } finally {
      setSiteSavingId(null);
    }
  };

  const addWorkCenter = async () => {
    const name = newWorkCenter.trim();
    if (!name || workCenters.includes(name)) return;
    try {
      await axios.put(`${API}/admin/work-centers`, { work_centers: [...workCenters, name] });
      setNewWorkCenter("");
      toast.success(`Work Center "${name}" added`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add Work Center");
    }
  };

  const removeWorkCenter = async (name) => {
    if (!window.confirm(`Remove Work Center "${name}"? Any user assignments or operation mappings using it will just stop matching (nothing else is deleted).`)) return;
    try {
      await axios.put(`${API}/admin/work-centers`, { work_centers: workCenters.filter((w) => w !== name) });
      toast.success(`Work Center "${name}" removed`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to remove Work Center");
    }
  };

  const toggleEnforcement = async (enabled) => {
    setSavingEnforcement(true);
    try {
      await axios.put(`${API}/admin/work-centers/enforcement`, { enabled });
      setEnforceRestrictions(enabled);
      toast.success(enabled ? "User-level restrictions are now ENFORCED" : "User-level restrictions are OFF", {
        description: enabled
          ? "Rows lock again for users outside the operation's Department/Site, and until the previous operation is finished."
          : "Every user with page access can confirm any operation. Assignments are kept, just not enforced.",
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to change enforcement");
    } finally {
      setSavingEnforcement(false);
    }
  };

  const addSite = async () => {
    const name = newSite.trim();
    if (!name || sites.includes(name)) return;
    try {
      await axios.put(`${API}/admin/work-centers/sites`, { sites: [...sites, name] });
      setNewSite("");
      toast.success(`Site "${name}" added`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add Site");
    }
  };

  const removeSite = async (name) => {
    if (!window.confirm(`Remove Site "${name}"? Any user assignments using it will just stop matching (nothing else is deleted).`)) return;
    try {
      await axios.put(`${API}/admin/work-centers/sites`, { sites: sites.filter((s) => s !== name) });
      toast.success(`Site "${name}" removed`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to remove Site");
    }
  };

  const pairKey = (p) => `${p.site_id}::${p.department}`;

  const toggleUserWorkCenter = (userId, siteId, department) => {
    setWcDrafts((prev) => {
      const current = prev[userId] || [];
      const has = current.some((p) => p.site_id === siteId && p.department === department);
      return {
        ...prev,
        [userId]: has
          ? current.filter((p) => !(p.site_id === siteId && p.department === department))
          : [...current, { site_id: siteId, department }],
      };
    });
  };

  const isWcDirty = (u) => {
    const original = new Set((u.assigned_site_departments || []).map(pairKey));
    const draft = new Set((wcDrafts[u._id] || []).map(pairKey));
    return original.size !== draft.size || [...original].some((k) => !draft.has(k));
  };

  const saveUserWorkCenters = async (userId) => {
    setWcSavingId(userId);
    try {
      await axios.put(`${API}/admin/users/${userId}/work-centers`, { assigned_site_departments: wcDrafts[userId] || [] });
      toast.success("Work Center assignment updated");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update Work Center assignment");
    } finally {
      setWcSavingId(null);
    }
  };

  const searchTerm = userSearch.trim().toLowerCase();
  const visibleUsers = users.filter((u) => {
    const draft = drafts[u._id];
    const role = draft?.role || u.role || "user";
    const matchesAccess =
      accessFilter === "all" ||
      role === "admin" ||
      role === "super_admin" ||
      (draft?.allowed_pages || u.allowed_pages || []).includes(accessFilter);
    const matchesSearch =
      !searchTerm ||
      (u.name || "").toLowerCase().includes(searchTerm) ||
      (u.email || "").toLowerCase().includes(searchTerm);
    return matchesAccess && matchesSearch;
  });

  return (
    <div className="min-h-screen bg-[#F9FAFB] flex flex-col" data-testid="access-management-page">
      <Toaster position="top-right" />
      <header className="min-h-16 bg-[#0E7C86] shadow-[0_1px_3px_0_rgba(16,24,40,0.15)] flex items-center justify-between px-3 sm:px-5 shrink-0 z-10 gap-2 sm:gap-4">
        <div className="flex items-center gap-3 shrink-0" data-testid="app-title">
          <div className="w-8 h-8 rounded-lg bg-white/15 flex items-center justify-center shrink-0">
            <Shield size={18} weight="fill" className="text-white" />
          </div>
          <div className="flex flex-col leading-tight">
            <span className="font-heading text-[16px] font-bold text-white tracking-tight">Production Control</span>
            <span className="font-sans text-[12px] text-white/70 hidden sm:inline">Access Management</span>
          </div>
        </div>
        <div className="w-px h-7 bg-white/25 shrink-0" />
        <div className="flex items-center gap-3 flex-1 justify-start min-w-0">
          <NavTabs />
        </div>
        <SapConnectionStatus />
        <ErpConnectionStatus />
      </header>

      <div className="p-4 sm:p-6">
        <div className="flex items-center gap-2 mb-4">
          <ShieldCheck size={20} weight="fill" className="text-[#0E7C86]" />
          <h1 className="font-heading text-lg font-bold text-[#101828]">IT Access Management</h1>
        </div>

        <Tabs defaultValue="access" className="space-y-4">
          <TabsList className="flex-wrap h-auto" data-testid="access-management-tabs">
            <TabsTrigger value="access" data-testid="tab-user-access">User Access</TabsTrigger>
            <TabsTrigger value="store-assignment" data-testid="tab-store-assignment">Store Assignment</TabsTrigger>
            <TabsTrigger value="workcenter-assignment" data-testid="tab-workcenter-assignment">Work Center Assignment</TabsTrigger>
            <TabsTrigger value="floor-logins" data-testid="tab-floor-logins">Floor Logins</TabsTrigger>
          </TabsList>

          <TabsContent value="floor-logins" className="space-y-4">
            <FloorLoginsTab />
          </TabsContent>

          <TabsContent value="access" className="space-y-4">
            <p className="text-sm text-[#667085]">
              Grant or revoke page-level access for anyone who has signed in with Microsoft. New sign-ins appear here
              automatically with no access until you grant it.
            </p>

            <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6" data-testid="access-management-access-filter-bar">
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold text-[#344054] uppercase font-heading">Filter by Access Right</span>
                <Select value={accessFilter} onValueChange={setAccessFilter}>
                  <SelectTrigger className="h-8 w-full sm:w-[220px] text-xs bg-white" data-testid="access-management-access-filter-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Users</SelectItem>
                    {WORK_CENTER_GROUPS.flatMap((group) => group.keys).concat(OTHER_PAGE_KEYS).map((pageKey) => (
                      <SelectItem key={pageKey} value={pageKey}>{PAGE_LABELS[pageKey] || pageKey}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center gap-2">
                <span className="text-xs font-bold text-[#344054] uppercase font-heading shrink-0">Search User</span>
                <div className="relative w-full sm:w-[280px]">
                  <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#98A2B3] pointer-events-none" />
                  <input
                    type="text"
                    value={userSearch}
                    onChange={(e) => setUserSearch(e.target.value)}
                    placeholder="Search by user name or email..."
                    className="h-8 w-full rounded-md border border-[#D0D5DD] bg-white pl-8 pr-8 text-xs text-[#101828] placeholder:text-[#98A2B3] focus:outline-none focus:ring-2 focus:ring-[#0E7C86]/30 focus:border-[#0E7C86] transition-colors"
                    data-testid="access-management-user-search-input"
                  />
                  {userSearch && (
                    <button
                      type="button"
                      onClick={() => setUserSearch("")}
                      aria-label="Clear search"
                      className="absolute right-1.5 top-1/2 -translate-y-1/2 p-1 rounded text-[#98A2B3] hover:text-[#344054] hover:bg-[#F2F4F7] transition-colors"
                      data-testid="access-management-user-search-clear"
                    >
                      <X size={12} weight="bold" />
                    </button>
                  )}
                </div>
              </div>

              {!loading && users.length > 0 && (
                <span className="text-xs text-[#667085]" data-testid="access-management-user-count">
                  {visibleUsers.length} of {users.length} {users.length === 1 ? "user" : "users"}
                </span>
              )}
            </div>

            {loading ? (
              <div className="text-sm text-[#667085]" data-testid="access-management-loading">Loading users...</div>
            ) : users.length === 0 ? (
              <div className="text-sm text-[#667085]" data-testid="access-management-empty">No one has signed in yet.</div>
            ) : visibleUsers.length === 0 ? (
              <div className="text-sm text-[#667085]" data-testid="access-management-no-match">
                No users found matching your search.
              </div>
            ) : (
              <div className="bg-white border border-[#E4E7EC] rounded-xl overflow-hidden">
                {visibleUsers
                  .map((u) => {
                  const draft = drafts[u._id] || { role: "user", allowed_pages: [] };
                  const hasAllPages = draft.role === "admin" || draft.role === "super_admin";
                  const isSelf = currentUser && u.email === currentUser.email;
                  const viewerIsSuperAdmin = currentUser?.role === "super_admin";
                  return (
                    <div
                      key={u._id}
                      className="border-b border-[#E4E7EC] last:border-b-0 p-4"
                      data-testid={`access-management-row-${u._id}`}
                    >
                      <div className="flex items-center justify-between gap-4 flex-wrap">
                        <div>
                          <div className="text-sm font-bold text-[#101828]">
                            {u.name || u.email}
                            {isSelf && <span className="ml-2 text-xs text-[#98A2B3] font-normal">(you)</span>}
                          </div>
                          <div className="text-xs text-[#667085]">{u.email}</div>
                        </div>
                        <div className="flex items-center gap-3">
                          <Badge className={ROLE_BADGE[draft.role].className} data-testid={`access-management-role-badge-${u._id}`}>
                            {ROLE_BADGE[draft.role].label}
                          </Badge>
                          {viewerIsSuperAdmin ? (
                            <Select value={draft.role} onValueChange={(v) => setRole(u._id, v)} disabled={isSelf}>
                              <SelectTrigger className="h-8 w-[140px] text-xs bg-white" data-testid={`access-management-role-select-${u._id}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="user">User</SelectItem>
                                <SelectItem value="admin">Admin</SelectItem>
                                <SelectItem value="super_admin">Super Admin</SelectItem>
                              </SelectContent>
                            </Select>
                          ) : draft.role === "admin" ? (
                            <Button
                              variant="outline"
                              size="sm"
                              disabled={isSelf}
                              onClick={() => setRole(u._id, "user")}
                              className="text-xs border-[#D0D5DD]"
                              data-testid={`access-management-toggle-role-${u._id}`}
                            >
                              Demote to User
                            </Button>
                          ) : draft.role === "super_admin" ? (
                            <span className="text-xs text-[#98A2B3]" data-testid={`access-management-locked-${u._id}`}>
                              Only a super admin can change this
                            </span>
                          ) : null}
                        </div>
                      </div>

                      {!hasAllPages ? (
                        <div className="mt-3 space-y-3">
                          {WORK_CENTER_GROUPS.concat(OTHER_PAGE_KEYS.length ? [{ label: "Other", keys: OTHER_PAGE_KEYS }] : []).map((group) => (
                            <div key={group.label} data-testid={`access-management-group-${u._id}-${group.label.replace(/\s+/g, "-").toLowerCase()}`}>
                              <div className="text-[11px] font-bold text-[#98A2B3] uppercase font-heading mb-1.5 tracking-wide">
                                {group.label}
                              </div>
                              <div className="flex flex-wrap gap-x-5 gap-y-2">
                                {group.keys.map((pageKey) => (
                                  <label
                                    key={pageKey}
                                    className="flex items-center gap-2 text-sm text-[#344054] cursor-pointer"
                                    data-testid={`access-management-page-checkbox-label-${u._id}-${pageKey}`}
                                  >
                                    <Checkbox
                                      checked={draft.allowed_pages.includes(pageKey)}
                                      onCheckedChange={() => togglePage(u._id, pageKey)}
                                      data-testid={`access-management-page-checkbox-${u._id}-${pageKey}`}
                                    />
                                    {PAGE_LABELS[pageKey] || pageKey}
                                  </label>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="mt-2 text-xs text-[#667085]">
                          {draft.role === "admin" ? "Admins" : "Super Admins"} automatically have every page.
                        </div>
                      )}

                      {isDirty(u) && (
                        <div className="mt-3">
                          <Button
                            size="sm"
                            disabled={savingId === u._id}
                            onClick={() => save(u._id)}
                            className="bg-[#0E7C86] hover:bg-[#0B6B74] text-white text-xs"
                            data-testid={`access-management-save-${u._id}`}
                          >
                            {savingId === u._id ? "Saving..." : "Save Changes"}
                          </Button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </TabsContent>

          <TabsContent value="store-assignment" className="space-y-4">
            <p className="text-sm text-[#667085]">
              Restrict a store user to only the site(s) checked below - their Store Approval queue, request table, and
              site dropdown will only ever show those sites. Admins and Super Admins always see every site, so they
              don't appear here.
            </p>

            {loading ? (
              <div className="text-sm text-[#667085]" data-testid="store-assignment-loading">Loading users...</div>
            ) : (
              <div className="bg-white border border-[#E4E7EC] rounded-xl overflow-hidden">
                {users.filter((u) => (u.role || "user") === "user").length === 0 ? (
                  <div className="p-4 text-sm text-[#667085]" data-testid="store-assignment-empty">
                    No "User" role accounts yet - promote/demote roles on the User Access tab first.
                  </div>
                ) : (
                  users.filter((u) => (u.role || "user") === "user").map((u) => {
                    const boundDraft = siteDrafts[u._id] || [];
                    return (
                      <div
                        key={u._id}
                        className="border-b border-[#E4E7EC] last:border-b-0 p-4"
                        data-testid={`store-assignment-row-${u._id}`}
                      >
                        <div className="flex items-center justify-between gap-4 flex-wrap mb-2">
                          <div>
                            <div className="text-sm font-bold text-[#101828]">{u.name || u.email}</div>
                            <div className="text-xs text-[#667085]">{u.email}</div>
                          </div>
                          <Badge
                            className={boundDraft.length ? "bg-[#ECFDF3] text-[#027A48] border border-[#ABEFC6]" : "bg-[#F2F4F7] text-[#667085]"}
                            data-testid={`store-assignment-count-badge-${u._id}`}
                          >
                            <MapPin size={11} weight="bold" className="mr-1" />
                            {boundDraft.length ? `${boundDraft.length} site${boundDraft.length === 1 ? "" : "s"} bound` : "No sites bound"}
                          </Badge>
                        </div>
                        {knownSites.length === 0 ? (
                          <p className="text-xs text-[#98A2B3]">No sites found yet - visit Inventory or Store Approval once to populate the site list.</p>
                        ) : (
                          <div className="flex flex-wrap gap-x-5 gap-y-2">
                            {knownSites.map((site) => (
                              <label
                                key={site}
                                className="flex items-center gap-2 text-sm text-[#344054] cursor-pointer"
                                data-testid={`store-assignment-site-checkbox-label-${u._id}-${site}`}
                              >
                                <Checkbox
                                  checked={boundDraft.includes(site)}
                                  onCheckedChange={() => toggleSite(u._id, site)}
                                  data-testid={`store-assignment-site-checkbox-${u._id}-${site}`}
                                />
                                {site}
                              </label>
                            ))}
                          </div>
                        )}
                        {isSiteDirty(u) && (
                          <div className="mt-3">
                            <Button
                              size="sm"
                              disabled={siteSavingId === u._id}
                              onClick={() => saveSites(u._id)}
                              className="bg-[#0E7C86] hover:bg-[#0B6B74] text-white text-xs"
                              data-testid={`store-assignment-save-${u._id}`}
                            >
                              {siteSavingId === u._id ? "Saving..." : "Save Site Binding"}
                            </Button>
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            )}
          </TabsContent>

          <TabsContent value="workcenter-assignment" className="space-y-4">
            <div
              className={`rounded-xl border p-4 flex items-start justify-between gap-4 ${
                enforceRestrictions ? "bg-[#FFFAEB] border-[#FEDF89]" : "bg-[#ECFDF3] border-[#A6F4C5]"
              }`}
              data-testid="enforcement-switch-card"
            >
              <div className="space-y-1">
                <h3 className="text-sm font-bold text-[#101828]">
                  {enforceRestrictions ? "User-level restrictions are ENFORCED" : "User-level restrictions are OFF (testing period)"}
                </h3>
                <p className="text-xs text-[#475467] max-w-3xl">
                  {enforceRestrictions
                    ? "A Production Confirmation row locks for a user if its operation's (Site, Department) pair isn't checked below for them, OR an earlier operation on the same lot isn't finished yet."
                    : "Everyone with Production Confirmation access can confirm any operation, in any order - nothing locks. The assignments below are kept exactly as they are, just not enforced, so you can turn this back on the moment the floor is settled."}
                </p>
              </div>
              <Button
                size="sm"
                variant={enforceRestrictions ? "outline" : "default"}
                disabled={savingEnforcement}
                onClick={() => toggleEnforcement(!enforceRestrictions)}
                data-testid="enforcement-toggle-button"
                className="shrink-0"
              >
                {savingEnforcement ? "Saving..." : enforceRestrictions ? "Turn restrictions OFF" : "Turn restrictions ON"}
              </Button>
            </div>

            <p className="text-sm text-[#667085]">
              Operation-wise task assignment - not a SAP feature, built entirely in this app. A row on the Production
              Confirmation page locks for a user if its BOO operation's (Site, Department) pair isn't checked below
              for them, OR an earlier operation on the same lot isn't finished yet. Admins/Super Admins always see
              everything. An operation with no Department mapped (see Bill of Operations page), or a user with
              nothing checked, stays fully open (safe default). All of it only applies while the switch above is ON.
            </p>

            <div className="grid sm:grid-cols-2 gap-4">
              <div className="bg-white border border-[#E4E7EC] rounded-xl p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <Factory size={16} weight="bold" className="text-[#0E7C86]" />
                  <h3 className="text-sm font-bold text-[#101828]">Work Centers</h3>
                </div>
                <div className="flex flex-wrap gap-2" data-testid="workcenter-list">
                  {workCenters.map((wc) => (
                    <span key={wc} className="inline-flex items-center gap-1.5 bg-[#F2F4F7] text-[#344054] text-xs rounded-full pl-3 pr-1.5 py-1" data-testid={`workcenter-chip-${wc}`}>
                      {wc}
                      <button type="button" onClick={() => removeWorkCenter(wc)} className="hover:text-[#B42318]" data-testid={`workcenter-remove-${wc}`}>
                        <X size={12} weight="bold" />
                      </button>
                    </span>
                  ))}
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="text" value={newWorkCenter} onChange={(e) => setNewWorkCenter(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && addWorkCenter()}
                    placeholder="New Work Center name..." data-testid="workcenter-new-input"
                    className="flex-1 max-w-xs px-3 py-1.5 text-sm border border-[#D0D5DD] rounded-sm focus:outline-none focus:ring-1 focus:ring-[#0E7C86]"
                  />
                  <Button size="sm" variant="outline" onClick={addWorkCenter} data-testid="workcenter-add-button">
                    <Plus size={13} className="mr-1" /> Add
                  </Button>
                </div>
              </div>

              <div className="bg-white border border-[#E4E7EC] rounded-xl p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <MapPin size={16} weight="bold" className="text-[#0E7C86]" />
                  <h3 className="text-sm font-bold text-[#101828]">Sites</h3>
                </div>
                <p className="text-xs text-[#98A2B3]">
                  Pick from the real Sites already known from the Store Assignment tab above - a user's Work Center
                  grant only applies at the exact Site(s) checked for them below, matched against the BOO's own Site.
                </p>
                <div className="flex flex-wrap gap-2" data-testid="site-list">
                  {sites.map((s) => (
                    <span key={s} className="inline-flex items-center gap-1.5 bg-[#F2F4F7] text-[#344054] text-xs rounded-full pl-3 pr-1.5 py-1" data-testid={`site-chip-${s}`}>
                      {s}
                      <button type="button" onClick={() => removeSite(s)} className="hover:text-[#B42318]" data-testid={`site-remove-${s}`}>
                        <X size={12} weight="bold" />
                      </button>
                    </span>
                  ))}
                </div>
                <div className="flex items-center gap-2">
                  <Select value={newSite} onValueChange={setNewSite}>
                    <SelectTrigger className="w-52 h-9 text-sm" data-testid="site-new-select">
                      <SelectValue placeholder={knownSites.filter((s) => !sites.includes(s)).length ? "Pick a Site..." : "All known Sites added"} />
                    </SelectTrigger>
                    <SelectContent>
                      {knownSites.filter((s) => !sites.includes(s)).map((s) => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button size="sm" variant="outline" onClick={addSite} disabled={!newSite} data-testid="site-add-button">
                    <Plus size={13} className="mr-1" /> Add
                  </Button>
                </div>
              </div>
            </div>

            {loading ? (
              <div className="text-sm text-[#667085]" data-testid="workcenter-assignment-loading">Loading users...</div>
            ) : (
              <div className="bg-white border border-[#E4E7EC] rounded-xl overflow-hidden">
                {users.filter((u) => (u.role || "user") === "user").length === 0 ? (
                  <div className="p-4 text-sm text-[#667085]" data-testid="workcenter-assignment-empty">
                    No "User" role accounts yet - promote/demote roles on the User Access tab first.
                  </div>
                ) : (
                  users.filter((u) => (u.role || "user") === "user").map((u) => {
                    const wcDraft = wcDrafts[u._id] || [];
                    const pairSet = new Set(wcDraft.map(pairKey));
                    return (
                      <div
                        key={u._id}
                        className="border-b border-[#E4E7EC] last:border-b-0 p-4"
                        data-testid={`workcenter-assignment-row-${u._id}`}
                      >
                        <div className="flex items-center justify-between gap-4 flex-wrap mb-2">
                          <div>
                            <div className="text-sm font-bold text-[#101828]">{u.name || u.email}</div>
                            <div className="text-xs text-[#667085]">{u.email}</div>
                          </div>
                          <Badge
                            className={wcDraft.length ? "bg-[#ECFDF3] text-[#027A48] border border-[#ABEFC6]" : "bg-[#F2F4F7] text-[#667085]"}
                            data-testid={`workcenter-assignment-count-badge-${u._id}`}
                          >
                            <Factory size={11} weight="bold" className="mr-1" />
                            {wcDraft.length ? `${wcDraft.length} grant${wcDraft.length === 1 ? "" : "s"}` : "Unrestricted"}
                          </Badge>
                        </div>
                        {sites.length === 0 || workCenters.length === 0 ? (
                          <p className="text-xs text-[#98A2B3]">Define at least one Site and one Work Center above first.</p>
                        ) : (
                          <div className="space-y-3">
                            {sites.map((siteId) => (
                              <div key={siteId} data-testid={`workcenter-assignment-site-block-${u._id}-${siteId}`}>
                                <div className="text-xs font-bold text-[#475467] mb-1.5 flex items-center gap-1">
                                  <MapPin size={12} weight="bold" className="text-[#0E7C86]" /> {siteId}
                                </div>
                                <div className="flex flex-wrap gap-x-5 gap-y-2 pl-4">
                                  {workCenters.map((wc) => (
                                    <label
                                      key={wc}
                                      className="flex items-center gap-2 text-sm text-[#344054] cursor-pointer"
                                      data-testid={`workcenter-assignment-checkbox-label-${u._id}-${siteId}-${wc}`}
                                    >
                                      <Checkbox
                                        checked={pairSet.has(`${siteId}::${wc}`)}
                                        onCheckedChange={() => toggleUserWorkCenter(u._id, siteId, wc)}
                                        data-testid={`workcenter-assignment-checkbox-${u._id}-${siteId}-${wc}`}
                                      />
                                      {wc}
                                    </label>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                        {isWcDirty(u) && (
                          <div className="mt-3">
                            <Button
                              size="sm"
                              disabled={wcSavingId === u._id}
                              onClick={() => saveUserWorkCenters(u._id)}
                              className="bg-[#0E7C86] hover:bg-[#0B6B74] text-white text-xs"
                              data-testid={`workcenter-assignment-save-${u._id}`}
                            >
                              {wcSavingId === u._id ? "Saving..." : "Save Work Center Assignment"}
                            </Button>
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
